import { Box, IconButton } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { ViewerControls } from './ViewerControls';
import { ZoomableImage } from './ZoomableImage';

type ImageViewerProps = {
    moveSelection: (direction: 'left' | 'right') => Promise<void>;
    showGrayScaleControls: boolean;
    url: string;
};

export const INITIAL_ZOOM = 1;
export const INITIAL_TRANSLATE = { x: 0, y: 0 };

export function ImageViewer({ moveSelection, showGrayScaleControls, url }: ImageViewerProps) {
    const initialPos = { x: null, y: null };
    const [imageInGrayScale, setImageInGrayScale] = useState(false);
    const [rotationDegrees, setRotationDegrees] = useState(0);
    const [scale, setScale] = useState(INITIAL_ZOOM);
    const [translate, setTranslate] = useState(INITIAL_TRANSLATE);
    const [previousPos, setPreviousPos] = useState<{ x: null | number; y: null | number }>(initialPos);
    const [isPanning, setIsPanning] = useState(false);

    useEffect(() => {
        function handleMousemove(e: MouseEvent) {
            // Prevent unneeded state updates by checking if position actually changed
            if (e.clientX !== previousPos.x || e.clientY !== previousPos.y) {
                if (isPanning) {
                    setTranslate({
                        x: previousPos.x !== null ? translate.x + e.clientX - previousPos.x : translate.x,
                        y: previousPos.y !== null ? translate.y + e.clientY - previousPos.y : translate.y,
                    });
                }
                setPreviousPos({ x: e.clientX, y: e.clientY });
            }
        }

        function handleMousedown() {
            setIsPanning(true);
        }

        function handleMouseup() {
            setIsPanning(false);
        }

        window.addEventListener('mousemove', handleMousemove);
        window.addEventListener('mousedown', handleMousedown);
        window.addEventListener('mouseup', handleMouseup);

        return () => {
            window.removeEventListener('mousemove', handleMousemove);
            window.removeEventListener('mousedown', handleMousedown);
            window.removeEventListener('mouseup', handleMouseup);
        };
    }, [isPanning]);

    const handleWheel = (e: { clientX: number; clientY: number; deltaY: number }) => {
        const scrollDampening = 50;
        const ratio = Math.atan(e.deltaY / scrollDampening) / (Math.PI / 2);

        const newScale = scale * (1 + ratio);
        if (newScale > 1) {
            setScale(scale * (1 + ratio));
        }
    };

    return (
        <Box
            alignItems=" center"
            display="flex"
            width="100%"
        >
            <IconButton
                icon="caret-left"
                onClick={async () => {
                    await moveSelection('left');
                }}
            />
            <StyledImageViewer onWheelCapture={handleWheel}>
                <ZoomableImage
                    imageClassName="zoomable-image"
                    imageInGrayScale={imageInGrayScale}
                    rotationDegrees={rotationDegrees}
                    src={url}
                    translate={translate}
                    zoomLevel={scale}
                />

                <ViewerControls
                    onImageInGrayScale={setImageInGrayScale}
                    rotationDegrees={rotationDegrees}
                    setRotationDegrees={setRotationDegrees}
                    setTranslate={setTranslate}
                    setZoomLevel={setScale}
                    showGrayScaleControls={showGrayScaleControls}
                    zoomLevel={scale}
                />
            </StyledImageViewer>
            <IconButton
                icon="caret-right"
                onClick={async () => {
                    await moveSelection('right');
                }}
            />
        </Box>
    );
}

const StyledImageViewer = styled.div`
    position: relative;
    height: 100%;
    width: 100%;
    text-align: center;
    overflow: hidden;
`;
